import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { aiApi, bookApi } from '@/api'
import type { Book, Chapter } from '@/types/user'
import type { ReadingAiStatus, ReadingBranchOption, ReadingChapter } from '@/types/reading'

type UseReadingAiBranchOptions = {
  book: Ref<Book>
  chapter: Ref<ReadingChapter>
  chapters: Ref<Chapter[]>
  currentChapter: Ref<number>
  route: RouteLocationNormalizedLoaded
  router: Router
  genreLabel: ComputedRef<string>
  isPublicCreatorBook: ComputedRef<boolean>
  canPublishAsCreator: ComputedRef<boolean>
  isEndOfChapter: ComputedRef<boolean | string>
  showChapterEnding: ComputedRef<boolean | string>
  ensureFreshReadingName: () => string | null
  formatGeneratedChapterTitle: (chapterNumber: number, title: string) => string
  loadChapterContent: () => Promise<void>
}

export const useReadingAiBranch = (options: UseReadingAiBranchOptions) => {
  const aiStatus = ref<ReadingAiStatus | null>(null)
  const isCheckingAI = ref(false)
  const branchOptions = ref<ReadingBranchOption[]>([])
  const selectedBranchIndex = ref<number | null>(null)
  const isGeneratingBranch = ref(false)
  const isConfirmingBranch = ref(false)
  const customBranchText = ref('')
  const showRewriteComposer = ref(false)
  const branchRequestKey = ref('')

  const isAIAvailable = computed(() => {
    return aiStatus.value?.deepseek.available === true
  })

  const aiStatusText = computed(() => {
    if (!aiStatus.value) return '检查中...'
    if (aiStatus.value.deepseek.available) return 'AI续写可用'
    return 'AI续写暂不可用'
  })

  const aiStatusColor = computed(() => {
    if (!aiStatus.value) return 'gray'
    if (aiStatus.value.deepseek.available) return 'green'
    return 'red'
  })

  const showBranchComposer = computed(() => {
    return !options.isPublicCreatorBook.value
      || showRewriteComposer.value
      || branchOptions.value.length > 0
      || isGeneratingBranch.value
      || isConfirmingBranch.value
  })

  const showPublicCreatorContinue = computed(() => {
    return options.isPublicCreatorBook.value
      && !showBranchComposer.value
      && options.chapter.value.hasAccess !== false
  })

  const shouldShowBranchStartButton = computed(() => {
    return branchOptions.value.length === 0
      && !isGeneratingBranch.value
      && !isConfirmingBranch.value
      && !options.canPublishAsCreator.value
  })

  const chapterEndTitle = computed(() => {
    if (showPublicCreatorContinue.value) return '继续追更，还是改写？'
    return '接下来，故事会走向哪里？'
  })

  const chapterEndDescription = computed(() => {
    if (showPublicCreatorContinue.value) {
      return '默认继续阅读创作者提交的章节；想走自己的线，可以轻轻改写剧情方向。'
    }
    return '选择一个方向，AI 将为你续写下一章'
  })

  const branchTitle = (index: number, fallback: string) => {
    const titles = ['燃爽突破', '情感升温', '反转危机']
    return fallback || titles[index] || '新的走向'
  }

  const resetBranchState = () => {
    branchOptions.value = []
    selectedBranchIndex.value = null
    customBranchText.value = ''
    showRewriteComposer.value = false
    branchRequestKey.value = ''
  }

  const generateBranchOptions = async (silent = false) => {
    if (!aiStatus.value?.deepseek.available) {
      if (!silent) alert('AI续写服务当前不可用，请稍后再试')
      return
    }

    try {
      isGeneratingBranch.value = true
      branchOptions.value = []
      selectedBranchIndex.value = null

      const response = await aiApi.generateBranchDirections({
        previousChapter: options.chapter.value.content,
        genre: options.genreLabel.value,
        numOptions: 3
      })

      if (response.code === 200 && response.data?.branches) {
        branchOptions.value = response.data.branches
      } else {
        throw new Error(response.message || '生成分支方向失败')
      }
    } catch (err: any) {
      console.error('生成剧情分支方向失败:', err)
      if (!silent) alert(`生成分支失败: ${err.message}`)
      branchOptions.value = []
    } finally {
      isGeneratingBranch.value = false
    }
  }

  const maybeGenerateBranchOptions = () => {
    if (!isAIAvailable.value || !showBranchComposer.value || !options.isEndOfChapter.value || !options.showChapterEnding.value) return
    if (branchOptions.value.length > 0 || isGeneratingBranch.value || isConfirmingBranch.value) return

    const key = `${options.book.value.id}-${options.currentChapter.value}-${options.chapter.value.id}`
    if (branchRequestKey.value === key) return
    branchRequestKey.value = key
    generateBranchOptions(true)
  }

  const openRewriteComposer = () => {
    showRewriteComposer.value = true
    maybeGenerateBranchOptions()
  }

  const checkAIServiceStatus = async () => {
    try {
      isCheckingAI.value = true
      const response = await aiApi.getStatus()
      if (response.code === 200 && response.data) {
        aiStatus.value = response.data
        console.log('AI服务状态:', aiStatus.value)
        maybeGenerateBranchOptions()
      }
    } catch (err) {
      console.warn('获取AI服务状态失败:', err)
      aiStatus.value = {
        deepseek: { available: false, error: '服务状态检查失败' },
        coverImage: { available: false, error: '服务状态检查失败' }
      }
    } finally {
      isCheckingAI.value = false
    }
  }

  const continueWithChoice = async (choiceText: string, choiceType: 'ai' | 'custom', optionIndex: number | null = null) => {
    if (!aiStatus.value?.deepseek.available) {
      alert('AI续写服务当前不可用，请稍后再试')
      return
    }

    const normalizedChoice = choiceText.trim()
    if (!normalizedChoice) return

    try {
      isConfirmingBranch.value = true
      selectedBranchIndex.value = optionIndex
      const requestedForkTitle = options.ensureFreshReadingName()
      if (requestedForkTitle === null) return

      const nextChapterNumber = options.currentChapter.value + 1
      const generatedChapterTitle = options.formatGeneratedChapterTitle(nextChapterNumber, normalizedChoice.slice(0, 30))

      const genResponse = await aiApi.generateChapter({
        previousChapter: options.chapter.value.content,
        chapterTitle: generatedChapterTitle,
        genre: options.genreLabel.value,
        targetLength: 800,
        style: `以下一个方向展开剧情：${normalizedChoice}`
      })

      const generatedContent = genResponse.data?.content || genResponse.data?.continuation
      if (!generatedContent) {
        throw new Error('AI生成内容为空')
      }

      const createResponse = await bookApi.forkChapter(options.book.value.id, {
        chapterNumber: nextChapterNumber,
        title: generatedChapterTitle,
        content: generatedContent,
        choiceType,
        choiceText: normalizedChoice,
        forkTitle: requestedForkTitle || undefined,
        forceNew: options.route.query.new === '1'
      })

      if (createResponse.code === 200 && createResponse.data?.chapter) {
        if (createResponse.data.book) {
          options.book.value = createResponse.data.book
        }

        resetBranchState()
        options.chapters.value.push(createResponse.data.chapter)
        options.currentChapter.value = nextChapterNumber
        options.router.push(`/reading/${options.book.value.id}/${nextChapterNumber}`)
        await options.loadChapterContent()
      } else {
        throw new Error(createResponse.message || '章节创建失败')
      }
    } catch (err: any) {
      console.error('生成下一章失败:', err)
      alert(`续写失败: ${err.message}`)
      selectedBranchIndex.value = null
    } finally {
      isConfirmingBranch.value = false
    }
  }

  const confirmBranch = async (index: number) => {
    const option = branchOptions.value[index]
    if (!option) return
    await continueWithChoice(option.summary, 'ai', index)
  }

  const confirmCustomBranch = async () => {
    const text = customBranchText.value.trim()
    if (text.length < 6) {
      alert('剧情方向再写具体一点点吧')
      return
    }
    await continueWithChoice(text, 'custom')
  }

  return {
    aiStatus,
    isCheckingAI,
    branchOptions,
    selectedBranchIndex,
    isGeneratingBranch,
    isConfirmingBranch,
    customBranchText,
    showRewriteComposer,
    isAIAvailable,
    aiStatusText,
    aiStatusColor,
    showBranchComposer,
    showPublicCreatorContinue,
    shouldShowBranchStartButton,
    chapterEndTitle,
    chapterEndDescription,
    branchTitle,
    resetBranchState,
    openRewriteComposer,
    checkAIServiceStatus,
    maybeGenerateBranchOptions,
    generateBranchOptions,
    confirmBranch,
    confirmCustomBranch
  }
}
